import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, User, MapPin, CreditCard, Banknote, MessageCircle, Tag, Check, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { calculateCartSubtotal } from '../utils/priceUtils';
import { toast } from 'sonner';
import { trackPurchase, trackInitiateCheckout,  trackAddPaymentInfo } from "../utils/Pixel";
import React, { useEffect } from "react";

const Checkout = () => {
  const { cartItems, clearCart } = useCart();
  const { t, getProductName, getWeight, getCutType, getPackagingOption, formatLocalPrice, language, isRTL } = useLanguage();
  const navigate = useNavigate();
  
  // Helper to convert numbers to Arabic numerals
  const toArabicNum = (num) => {
    if (!isRTL) return num;
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return String(num).replace(/[0-9]/g, (d) => arabicNumerals[parseInt(d)]);
  };
  
  const subtotal = calculateCartSubtotal(cartItems);
  const deliveryFee = 50;
  useEffect(() => {
  trackInitiateCheckout({
    value: Number(subtotal) || 0,
    currency: "EGP",
    num_items: cartItems.reduce((sum, item) => sum + item.quantity, 0),
    content_ids: cartItems.map(item =>
      String(item.id || item.sku || item.name?.en || item.name)
    ),
  });
  // fire once when checkout page opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // Promo code system
  const promoCodes = {
    'INFLUENCER5': { discount: 5, nameEn: 'Influencer Discount', nameAr: 'خصم المؤثرين', mincedOnly: false },
    'ELITE10': { discount: 10, nameEn: 'Launch Discount', nameAr: 'خصم الافتتاح', mincedOnly: false },
    'MINCED20': { discount: 20, nameEn: 'Minced Meat Special', nameAr: 'عرض اللحم المفروم', mincedOnly: true }
  };

  // Minced meat product identifiers (names contain these keywords)
  const mincedMeatKeywords = ['Ground Beef', 'Minced', 'مفروم'];

  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');

  // Calculate discount - MINCED20 only applies to minced meat products
  const calculateDiscount = () => {
    if (!appliedPromo) return 0;
    
    if (appliedPromo.mincedOnly) {
      // Calculate subtotal of minced meat items only
      const mincedSubtotal = cartItems
        .filter(item => {
          const itemName = typeof item.name === 'string' ? item.name : item.name.en || '';
          return mincedMeatKeywords.some(keyword => 
            itemName.toLowerCase().includes(keyword.toLowerCase())
          );
        })
        .reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      return Math.round((mincedSubtotal * appliedPromo.discount) / 100);
    }
    
    // For other promo codes, apply to entire subtotal
    return Math.round((subtotal * appliedPromo.discount) / 100);
  };

  const discountAmount = calculateDiscount();
  const total = subtotal - discountAmount + deliveryFee;

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    governorate: '',
    city: '',
    address: '',
    notes: '',
    paymentMethod: 'cod' // cod or visa
  });

  const [cardData, setCardData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const egyptGovernorates = [
    'Cairo', 'Alexandria', 'Giza', 'Qalyubia', 'Port Said', 'Suez',
    'Luxor', 'Aswan', 'Asyut', 'Beheira', 'Beni Suef', 'Dakahlia',
    'Damietta', 'Faiyum', 'Gharbia', 'Ismailia', 'Kafr El Sheikh',
    'Matrouh', 'Minya', 'Monufia', 'New Valley', 'North Sinai',
    'Qena', 'Red Sea', 'Sharqia', 'Sohag', 'South Sinai'
  ];

  const egyptGovernoratesAr = {
    'Cairo': 'القاهرة',
    'Alexandria': 'الإسكندرية',
    'Giza': 'الجيزة',
    'Qalyubia': 'القليوبية',
    'Port Said': 'بورسعيد',
    'Suez': 'السويس',
    'Luxor': 'الأقصر',
    'Aswan': 'أسوان',
    'Asyut': 'أسيوط',
    'Beheira': 'البحيرة',
    'Beni Suef': 'بني سويف',
    'Dakahlia': 'الدقهلية',
    'Damietta': 'دمياط',
    'Faiyum': 'الفيوم',
    'Gharbia': 'الغربية',
    'Ismailia': 'الإسماعيلية',
    'Kafr El Sheikh': 'كفر الشيخ',
    'Matrouh': 'مطروح',
    'Minya': 'المنيا',
    'Monufia': 'المنوفية',
    'New Valley': 'الوادي الجديد',
    'North Sinai': 'شمال سيناء',
    'Qena': 'قنا',
    'Red Sea': 'البحر الأحمر',
    'Sharqia': 'الشرقية',
    'Sohag': 'سوهاج',
    'South Sinai': 'جنوب سيناء'
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-stone-50 py-20" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShoppingBag className="mx-auto h-24 w-24 text-gray-400 mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('emptyCart')}</h2>
          <p className="text-lg text-gray-600 mb-8">
            {language === 'ar' ? 'أضف بعض المنتجات إلى سلتك قبل إتمام الطلب.' : 'Add some products to your cart before checking out.'}
          </p>
          <Button asChild size="lg" className="bg-red-600 hover:bg-red-700 text-white">
            <a href="/products">{t('viewProducts')}</a>
          </Button>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCardChange = (e) => {
    setCardData({
      ...cardData,
      [e.target.name]: e.target.value
    });
  };

  const applyPromoCode = () => {
    const code = promoCode.toUpperCase().trim();
    setPromoError('');
    
    if (!code) {
      setPromoError(language === 'ar' ? 'يرجى إدخال كود الخصم' : 'Please enter a promo code');
      return;
    }

    if (promoCodes[code]) {
      const promoConfig = promoCodes[code];
      setAppliedPromo({ code, ...promoConfig });
      
      // Show appropriate message for minced-only codes
      const toastDescription = promoConfig.mincedOnly 
        ? (language === 'ar' 
            ? `${promoConfig.discount}% خصم على اللحم المفروم فقط` 
            : `${promoConfig.discount}% discount on minced meat only`)
        : `${promoConfig.discount}% ${language === 'ar' ? 'خصم' : 'discount'}`;
      
      toast.success(
        language === 'ar' ? 'تم تطبيق كود الخصم!' : 'Promo code applied!',
        { description: toastDescription }
      );
    } else {
      setPromoError(language === 'ar' ? 'كود الخصم غير صالح' : 'Invalid promo code');
      setAppliedPromo(null);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
    toast.info(language === 'ar' ? 'تم إزالة كود الخصم' : 'Promo code removed');
  };

  const generateWhatsAppMessage = () => {
    const orderItems = cartItems.map(item => 
      `• ${getProductName(item.name)} - ${getWeight(item.selectedWeight)} x${toArabicNum(item.quantity)} = ${formatLocalPrice(item.price * item.quantity)} ${language === 'ar' ? 'ج.م' : 'EGP'}`
    ).join('\n');

    const paymentMethodText = formData.paymentMethod === 'cod' 
      ? (language === 'ar' ? 'الدفع عند الاستلام' : 'Cash on Delivery')
      : (language === 'ar' ? 'فيزا / بطاقة ائتمان' : 'Visa / Credit Card');

    const promoText = appliedPromo 
      ? (language === 'ar' 
        ? `\n🎟️ *كود الخصم:* ${appliedPromo.code} (${toArabicNum(appliedPromo.discount)}% - ${appliedPromo.nameAr})\nالخصم: -${formatLocalPrice(discountAmount)} ج.م`
        : `\n🎟️ *Promo Code:* ${appliedPromo.code} (${appliedPromo.discount}% - ${appliedPromo.nameEn})\nDiscount: -${formatLocalPrice(discountAmount)} EGP`)
      : '';

    const message = language === 'ar' 
      ? `🥩 *طلب جديد من EliteMeat Farm*

👤 *معلومات العميل:*
الاسم: ${formData.fullName}
الهاتف: ${formData.phone}
${formData.email ? `البريد: ${formData.email}` : ''}

📍 *عنوان التوصيل:*
${egyptGovernoratesAr[formData.governorate] || formData.governorate}، ${formData.city}
${formData.address}

🛒 *تفاصيل الطلب:*
${orderItems}

💰 *الإجمالي:*
المجموع الفرعي: ${formatLocalPrice(subtotal)} ج.م${promoText}
التوصيل: ${deliveryFee === 0 ? 'مجاني' : formatLocalPrice(deliveryFee) + ' ج.م'}
*الإجمالي النهائي: ${formatLocalPrice(total)} ج.م*

💳 *طريقة الدفع:* ${paymentMethodText}

${formData.notes ? `📝 *ملاحظات:* ${formData.notes}` : ''}`
      : `🥩 *New Order from EliteMeat Farm*

👤 *Customer Information:*
Name: ${formData.fullName}
Phone: ${formData.phone}
${formData.email ? `Email: ${formData.email}` : ''}

📍 *Delivery Address:*
${formData.governorate}, ${formData.city}
${formData.address}

🛒 *Order Details:*
${orderItems}

💰 *Pricing:*
Subtotal: ${formatLocalPrice(subtotal)} EGP${promoText}
Delivery: ${deliveryFee === 0 ? 'FREE' : formatLocalPrice(deliveryFee) + ' EGP'}
*Final Total: ${formatLocalPrice(total)} EGP*

💳 *Payment Method:* ${paymentMethodText}

${formData.notes ? `📝 *Notes:* ${formData.notes}` : ''}`;

    return encodeURIComponent(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    if (!formData.fullName || !formData.phone || !formData.governorate || !formData.city || !formData.address) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      setIsSubmitting(false);
      return;
    }

    // Validate card data if visa selected
    if (formData.paymentMethod === 'visa') {
      if (!cardData.cardNumber || !cardData.expiryDate || !cardData.cvv) {
        toast.error(language === 'ar' ? 'يرجى إدخال بيانات البطاقة' : 'Please enter card details');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Generate WhatsApp message and open WhatsApp
      const message = generateWhatsAppMessage();
      const whatsappNumber = '201270706065'; // Egypt phone number format
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;
      
      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank');
      trackPurchase({
  value: Number(total) || 0,
  currency: "EGP",
  num_items: cartItems.reduce((sum, item) => sum + item.quantity, 0),
  content_ids: cartItems.map((item) => String(item.id || item.sku || item.name?.en || item.name)),
});
      
      // Show success message
      toast.success(
        language === 'ar' ? 'تم إرسال الطلب!' : 'Order Sent!',
        {
          description: language === 'ar' 
            ? 'سيتم تأكيد طلبك عبر واتساب'
            : 'Your order will be confirmed via WhatsApp'
        }
      );
      
      // Clear cart after a delay
      setTimeout(() => {
        clearCart();
        navigate('/order-success');
      }, 2000);
      
    } catch (error) {
      toast.error(
        language === 'ar' ? 'حدث خطأ' : 'Error occurred',
        {
          description: language === 'ar' 
            ? 'يرجى المحاولة مرة أخرى'
            : 'Please try again'
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <section className="bg-stone-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-2">{t('checkout')}</h1>
          <p className="text-gray-300">
            {language === 'ar' ? 'أكمل طلبك' : 'Complete your order'}
          </p>
        </div>
      </section>

      {/* Checkout Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Customer Information */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <User className="text-red-600" size={24} />
                      {t('customerInfo')}
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('fullName')} *
                        </label>
                        <Input
                          type="text"
                          name="fullName"
                          required
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder={language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                          data-testid="checkout-fullname"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('phone')} *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="01XXXXXXXXX"
                          data-testid="checkout-phone"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('email')}
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder={language === 'ar' ? 'بريدك@email.com' : 'your@email.com'}
                          data-testid="checkout-email"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <MapPin className="text-red-600" size={24} />
                      {t('address')}
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {language === 'ar' ? 'المحافظة' : 'Governorate'} *
                        </label>
                        <select
                          name="governorate"
                          required
                          value={formData.governorate}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                          data-testid="checkout-governorate"
                        >
                          <option value="">{language === 'ar' ? 'اختر المحافظة' : 'Select Governorate'}</option>
                          {egyptGovernorates.map((gov) => (
                            <option key={gov} value={gov}>
                              {language === 'ar' ? egyptGovernoratesAr[gov] : gov}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('city')} *
                        </label>
                        <Input
                          type="text"
                          name="city"
                          required
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder={language === 'ar' ? 'أدخل المدينة' : 'Enter your city'}
                          data-testid="checkout-city"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {language === 'ar' ? 'العنوان الكامل' : 'Full Address'} *
                        </label>
                        <Textarea
                          name="address"
                          required
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder={language === 'ar' ? 'الشارع، رقم المبنى، الطابق، الشقة...' : 'Street, building number, floor, apartment...'}
                          rows={3}
                          data-testid="checkout-address"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('notes')}
                        </label>
                        <Textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          placeholder={language === 'ar' ? 'أي تعليمات خاصة لطلبك...' : 'Any special instructions for your order...'}
                          rows={3}
                          data-testid="checkout-notes"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Promo Code Section */}
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <Tag className="text-red-600" size={24} />
                      {language === 'ar' ? 'كود الخصم' : 'Promo Code'}
                    </h2>
                    
                    {appliedPromo ? (
                      <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Check className="text-green-600" size={20} />
                          </div>
                          <div>
                            <div className="font-semibold text-green-800">
                              {appliedPromo.code}
                            </div>
                            <div className="text-sm text-green-600">
                              {language === 'ar' ? appliedPromo.nameAr : appliedPromo.nameEn} - {toArabicNum(appliedPromo.discount)}% {language === 'ar' ? 'خصم' : 'off'}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removePromoCode}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <Input
                            type="text"
                            value={promoCode}
                            onChange={(e) => {
                              setPromoCode(e.target.value);
                              setPromoError('');
                            }}
                            placeholder={language === 'ar' ? 'أدخل كود الخصم' : 'Enter promo code'}
                            className="flex-1"
                            data-testid="promo-code-input"
                          />
                          <Button
                            type="button"
                            onClick={applyPromoCode}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            {language === 'ar' ? 'تطبيق' : 'Apply'}
                          </Button>
                        </div>
                        {promoError && (
                          <p className="text-sm text-red-600">{promoError}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Method */}
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <CreditCard className="text-red-600" size={24} />
                      {t('paymentMethod')}
                    </h2>
                    
                    <div className="space-y-4">
                      {/* Cash on Delivery */}
                      <label className={`flex items-center gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.paymentMethod === 'cod' 
                          ? 'border-red-600 bg-red-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cod"
                          checked={formData.paymentMethod === 'cod'}
                          onChange={handleInputChange}
                          className="w-5 h-5 text-red-600"
                        />
                        <Banknote className="text-green-600" size={28} />
                        <div>
                          <div className="font-semibold text-gray-900">{t('cashOnDelivery')}</div>
                          <div className="text-sm text-gray-600">
                            {language === 'ar' ? 'ادفع نقداً عند استلام طلبك' : 'Pay cash when you receive your order'}
                          </div>
                        </div>
                      </label>

                      {/* Visa / Credit Card */}
                      <label className={`flex items-center gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.paymentMethod === 'visa' 
                          ? 'border-red-600 bg-red-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="visa"
                          checked={formData.paymentMethod === 'visa'}
                          onChange={(e) => {
  handleInputChange(e);
  trackAddPaymentInfo();
}}

                        
                          className="w-5 h-5 text-red-600"
                        />
                        <CreditCard className="text-blue-600" size={28} />
                        <div>
                          <div className="font-semibold text-gray-900">{t('visaCard')}</div>
                          <div className="text-sm text-gray-600">
                            {language === 'ar' ? 'ادفع باستخدام بطاقتك' : 'Pay using your card'}
                          </div>
                        </div>
                      </label>

                      {/* Card Details (shown only when visa is selected) */}
                      {formData.paymentMethod === 'visa' && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('cardNumber')} *
                            </label>
                            <Input
                              type="text"
                              name="cardNumber"
                              value={cardData.cardNumber}
                              onChange={handleCardChange}
                              placeholder="1234 5678 9012 3456"
                              maxLength={19}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('expiryDate')} *
                              </label>
                              <Input
                                type="text"
                                name="expiryDate"
                                value={cardData.expiryDate}
                                onChange={handleCardChange}
                                placeholder="MM/YY"
                                maxLength={5}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('cvv')} *
                              </label>
                              <Input
                                type="text"
                                name="cvv"
                                value={cardData.cvv}
                                onChange={handleCardChange}
                                placeholder="123"
                                maxLength={4}
                              />
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            {language === 'ar' 
                              ? '* سيتم التحقق من الدفع عند تأكيد الطلب'
                              : '* Payment will be verified upon order confirmation'}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <Card className="border-0 shadow-lg sticky top-24">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('orderSummary')}</h2>
                    
                    {/* Order Items */}
                    <div className="space-y-4 mb-6 max-h-60 overflow-y-auto">
                      {cartItems.map((item) => (
                        <div key={item.id} className="flex gap-3 pb-3 border-b border-stone-200">
                          <img
                            src={item.image}
                            alt={getProductName(item.name)}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm text-gray-900 truncate">{getProductName(item.name)}</h3>
                            <p className="text-xs text-gray-600">{getWeight(item.selectedWeight)}</p>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-xs text-gray-600">×{toArabicNum(item.quantity)}</span>
                              <span className="text-sm font-bold text-red-600">
                                {formatLocalPrice(item.price * item.quantity)} {t('egp')}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pricing */}
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-gray-700">
                        <span>{t('subtotal')}:</span>
                        <span className="font-semibold">{formatLocalPrice(subtotal)} {t('egp')}</span>
                      </div>
                      
                      {appliedPromo && (
                        <div className="flex justify-between text-green-600">
                          <span>
                            {language === 'ar' ? 'الخصم' : 'Discount'} ({toArabicNum(appliedPromo.discount)}%):
                          </span>
                          <span className="font-semibold">-{formatLocalPrice(discountAmount)} {t('egp')}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-gray-700">
                        <span>{t('shipping')}:</span>
                        <span className="font-semibold">
                          {deliveryFee === 0 ? (
                            <span className="text-green-600">{t('freeShipping')}</span>
                          ) : (
                            `${formatLocalPrice(deliveryFee)} ${t('egp')}`
                          )}
                        </span>
                      </div>
                      <div className="border-t border-stone-200 pt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-xl font-bold text-gray-900">{t('total')}:</span>
                          <span className="text-2xl font-bold text-red-600">{formatLocalPrice(total)} {t('egp')}</span>
                        </div>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-green-600 hover:bg-green-700 text-white h-14 text-lg flex items-center justify-center gap-2"
                      data-testid="place-order-btn"
                    >
                      <MessageCircle size={22} />
                      {isSubmitting 
                        ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') 
                        : t('placeOrder')
                      }
                    </Button>

                    <p className="text-xs text-gray-600 text-center mt-4">
                      {t('orderConfirmation')}
                    </p>

                    {/* WhatsApp Note */}
                    <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-800 text-center">
                        <MessageCircle className="inline-block mr-1" size={16} />
                        {language === 'ar' 
                          ? 'سيتم إرسال تفاصيل طلبك عبر واتساب للتأكيد'
                          : 'Your order details will be sent via WhatsApp for confirmation'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Checkout;
